import { useState } from 'react'
import {
  STATUS_LABEL,
  STATUS_TONE,
  STATUSES,
  updateTaskNotes,
  type Status,
  type TaskRow,
} from '../lib/api'
import { formatDate, relativeDue } from '../lib/grouping'
import LinkList from './LinkList'

interface Props {
  task: TaskRow
  /** Needed by LinkList, which calls the database directly. */
  token: string
  busy: boolean
  onStatus: (assignmentId: string, status: Status) => void
  onNote: (assignmentId: string, note: string) => void
  onLinksChanged: () => void
  /**
   * Whether to offer "Edit details" on the shared brief. Admins only (0021):
   * the brief is the manager's, and the database refuses anyone else.
   */
  canEditDetails?: boolean
  /**
   * "Create an issue" from this to-do — for a problem with it, or something to
   * bring up from doing it. Anyone assigned may (the database checks, 0019).
   * Left out where the caller has nowhere to send the issue form.
   */
  onCreateIssue?: (task: TaskRow) => void
}

export default function TaskCard({
  task,
  token,
  busy,
  onStatus,
  onNote,
  onLinksChanged,
  canEditDetails = false,
  onCreateIssue,
}: Props) {
  const [noteOpen, setNoteOpen] = useState(false)
  const [draft, setDraft] = useState(task.note ?? '')
  // The task's SHARED brief, distinct from `draft` above which is this person's
  // own note. Both are editable now, so the labels have to keep them apart.
  const [briefOpen, setBriefOpen] = useState(false)
  const [brief, setBrief] = useState(task.task_notes ?? '')
  const [briefBusy, setBriefBusy] = useState(false)
  const [briefProblem, setBriefProblem] = useState<string | null>(null)

  const saveBrief = async () => {
    setBriefBusy(true)
    setBriefProblem(null)
    try {
      await updateTaskNotes(token, task.task_id, brief)
      setBriefOpen(false)
      onLinksChanged()
    } catch (err) {
      setBriefProblem(err instanceof Error ? err.message : String(err))
    } finally {
      setBriefBusy(false)
    }
  }

  const overdue = task.status !== 'Completed' && task.days_until < 0
  const dueToday = task.status !== 'Completed' && task.days_until === 0
  const done = task.status === 'Completed'

  const noteChanged = draft.trim() !== (task.note ?? '').trim()

  return (
    <article
      className={
        'card' +
        (overdue ? ' card--overdue' : '') +
        (dueToday ? ' card--today' : '') +
        (done ? ' card--done' : '')
      }
    >
      <div className="card__head">
        <h3 className="card__title">{task.title}</h3>
        {task.priority !== 'Medium' && (
          <span className={`chip chip--${task.priority.toLowerCase()}`}>
            {task.priority}
          </span>
        )}
      </div>

      <p className="card__due">
        <span className="card__date">Due {formatDate(task.due_date)}</span>
        {!done && (
          <span className={overdue ? 'card__rel card__rel--late' : 'card__rel'}>
            {' · '}
            {relativeDue(task.days_until)}
          </span>
        )}
      </p>

      {/* The shared brief, which only a manager may change (0021). It used to be
          open to everyone on the task, and on a task shared by the whole team one
          person's answer replaced the brief with no name on it. Each person's own
          words go in their note below, or in an issue raised from the to-do. */}
      {briefOpen && canEditDetails ? (
        <div className="note">
          <textarea
            className="note__input"
            rows={3}
            autoFocus
            value={brief}
            placeholder="Details everyone on this task can see"
            onChange={(e) => setBrief(e.target.value)}
          />
          {briefProblem && <p className="banner banner--tight">{briefProblem}</p>}
          <div className="note__actions">
            <button
              type="button"
              className="btn btn--quiet"
              onClick={() => {
                setBrief(task.task_notes ?? '')
                setBriefProblem(null)
                setBriefOpen(false)
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn"
              disabled={briefBusy}
              onClick={() => void saveBrief()}
            >
              {briefBusy ? 'Saving…' : 'Save details'}
            </button>
          </div>
        </div>
      ) : (
        <>
          {task.task_notes && <p className="card__brief">{task.task_notes}</p>}
          {canEditDetails && (
            <button
              type="button"
              className="links__add"
              onClick={() => {
                setBrief(task.task_notes ?? '')
                setBriefOpen(true)
              }}
            >
              {task.task_notes ? 'Edit details' : '+ Add details'}
            </button>
          )}
        </>
      )}

      {/* Reference material sits above the status buttons: you read it before
          you act on the job, not after. This card is always the viewer's own
          assignment, so they can always add. */}
      <LinkList
        token={token}
        links={task.links ?? []}
        canEdit
        taskId={task.task_id}
        onChanged={onLinksChanged}
      />

      <div className="seg" role="group" aria-label="Status">
        {STATUSES.map((s) => (
          <button
            key={s}
            type="button"
            className={
              'seg__btn' +
              (task.status === s ? ` seg__btn--on seg__btn--${STATUS_TONE[s]}` : '')
            }
            aria-pressed={task.status === s}
            disabled={busy}
            onClick={() => {
              if (task.status !== s) onStatus(task.assignment_id, s)
            }}
          >
            {STATUS_LABEL[s]}
          </button>
        ))}
      </div>

      {noteOpen ? (
        <div className="note">
          <textarea
            className="note__input"
            value={draft}
            rows={3}
            placeholder="Your own notes or answer — only you and the managers see this"
            onChange={(e) => setDraft(e.target.value)}
          />
          <div className="note__actions">
            <button
              type="button"
              className="btn btn--quiet"
              onClick={() => {
                setDraft(task.note ?? '')
                setNoteOpen(false)
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn"
              disabled={busy || !noteChanged}
              onClick={() => {
                onNote(task.assignment_id, draft)
                setNoteOpen(false)
              }}
            >
              Save note
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          className="note__toggle"
          onClick={() => setNoteOpen(true)}
        >
          {task.note ? `Your note: ${task.note}` : '+ Add your note'}
        </button>
      )}

      {/* A note is private; an issue is for the meeting. Something worth the
          team's time — a problem with this job, a process that gets in the way —
          goes here, linked back to this to-do. */}
      {onCreateIssue && (
        <button type="button" className="note__toggle" onClick={() => onCreateIssue(task)}>
          + Create an issue
        </button>
      )}
    </article>
  )
}
